import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildAuthUserLabelById } from '../../auth/domain/authUserDisplayLabels';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useEmployeesStore, withSelfOwner } from '../../team/store/useEmployeesStore';
import {
  getPromotionEventTypeLabel,
  getPromotionHistory,
  getPromotionHistoryCount,
  type PromotionEventRow,
  type PromotionEventType,
} from '../infra/promotionEventQueries';
import { toLocalStartOfDayIso, toLocalEndOfDayIso } from '@shared/vueltoDateTime';
import type { PromotionWithTargets } from '../domain/promotionTypes';

const HISTORY_PAGE_SIZE = 20;

export function usePromotionHistory(promotion: PromotionWithTargets | null) {
  const employeesRaw = useEmployeesStore((state) => state.employees);
  const selfOwner = useEmployeesStore((state) => state.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const sessionUser = useAuthStore((state) => state.session?.user ?? null);
  const authUserLabels = useMemo(
    () =>
      buildAuthUserLabelById({
        sessionUserId: sessionUser?.id,
        sessionUserEmail: sessionUser?.email,
        ownerAuthUserId: sessionUser?.id ?? null,
        employees,
      }),
    [employees, sessionUser?.email, sessionUser?.id]
  );

  const [rows, setRows] = useState<PromotionEventRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [eventFilter, setEventFilter] = useState<PromotionEventType | 'all'>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const buildFilters = useCallback(() => {
    const filters: {
      eventType?: PromotionEventType | 'all';
      fromCreatedAt?: string;
      toCreatedAt?: string;
    } = { eventType: eventFilter };
    if (fromDate.trim()) filters.fromCreatedAt = toLocalStartOfDayIso(fromDate);
    if (toDate.trim()) filters.toCreatedAt = toLocalEndOfDayIso(toDate);
    return filters;
  }, [eventFilter, fromDate, toDate]);

  const loadPage = useCallback(
    async (nextOffset: number, append: boolean) => {
      if (!promotion) return;
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const filters = buildFilters();
        const [page, count] = await Promise.all([
          getPromotionHistory(promotion.id, HISTORY_PAGE_SIZE, nextOffset, filters),
          getPromotionHistoryCount(promotion.id, filters),
        ]);
        setRows((current) => (append ? [...current, ...page] : page));
        setTotalCount(count);
        setOffset(nextOffset + page.length);
        setHasMore(page.length >= HISTORY_PAGE_SIZE);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [buildFilters, promotion]
  );

  useEffect(() => {
    if (!promotion) {
      setRows([]);
      setOffset(0);
      setHasMore(false);
      setTotalCount(0);
      return;
    }
    void loadPage(0, false);
  }, [loadPage, promotion]);

  const onApplyFilters = useCallback(() => {
    void loadPage(0, false);
  }, [loadPage]);

  const onLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    void loadPage(offset, true);
  }, [hasMore, loadPage, loadingMore, offset]);

  const actorLabel = useCallback(
    (userId: string | null | undefined) => {
      if (!userId) return 'Sistema';
      return authUserLabels[userId] ?? `Usuario ${userId.slice(0, 8)}…`;
    },
    [authUserLabels]
  );

  const eventOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      { value: 'create', label: getPromotionEventTypeLabel('create') },
      { value: 'update', label: getPromotionEventTypeLabel('update') },
      { value: 'activate', label: getPromotionEventTypeLabel('activate') },
      { value: 'deactivate', label: getPromotionEventTypeLabel('deactivate') },
      { value: 'delete', label: getPromotionEventTypeLabel('delete') },
    ],
    []
  );

  return {
    rows,
    loading,
    loadingMore,
    hasMore,
    totalCount,
    eventFilter,
    setEventFilter,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    onApplyFilters,
    onLoadMore,
    actorLabel,
    eventOptions,
  };
}
